import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledDateField, ControlledSelectField, ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import { ContractChangeStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { Grid, GridItem, Loader } from 'uikit';
import { formatDateToLocale, newDate } from 'utils';
import { requiredContractChangeApprovedRule } from 'utils/constants';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { statusItemFromId } from '../hooks/useAgreementStatuses';
import { useCreateAgreement } from '../hooks/useCreateAgreement';
import { useEditAgreementItem } from '../hooks/useEditAgreementItem';
import { useGetDialogWarningText } from '../hooks/useGetDialogWarningText';
import { useIsUniqueContractChangeInternalNumber } from '../hooks/useIsUniqueContractChangeInternalNumber';

export const AddOrEditAgreementModal = ({
    currentContractId,
    isModalOpened,
    isDisabledByProjectStatus,
    isDisabledByContractStatus,
    closeHandler,
    refreshHandler,
    refreshProject,
    isCreating = false,
    contractHasContractChanges = true,
    currentAgreementItemId,
    needUpdateCurrentPriceList = false,
    needUpdatePriceListHandler,
}: {
    currentContractId: number;
    isModalOpened: boolean;
    closeHandler: (state: boolean) => void;
    refreshHandler: () => void;
    refreshProject?: () => void;
    isDisabledByProjectStatus?: boolean;
    isDisabledByContractStatus?: boolean;
    isCreating?: boolean;
    contractHasContractChanges?: boolean;
    currentAgreementItemId?: number | null | undefined;
    needUpdateCurrentPriceList?: boolean;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
}) => {
    const { id: projectId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);
    const [isOpenStatusDialog, setIsOpenStatusDialog] = useState<boolean>(false);

    const { control, handleSubmit, reset, setError, clearErrors, formState, resetField } = useForm<any>({
        criteriaMode: 'all',
    });

    const internalNumber = useWatch({
        control,
        name: 'internalNumber',
    });

    const successCreate = useCallback(() => {
        if (!contractHasContractChanges) {
            refreshProject?.();
        }
    }, [contractHasContractChanges, refreshProject]);

    const {
        resetCreatingData,
        createAgreementStatus,
        createAgreementError,
        createAgreementRequest,
        isCreatingInProgress,
        createAgreementData,
    } = useCreateAgreement({
        onSuccess: successCreate,
    });

    const {
        agreementItemData,
        isFetchingDataForDisplayInProgress,
        resetEditData,
        agreementEditData,
        agreementEditStatus,
        agreementEditError,
        agreementEditRequest,
        isSavingInProgress,
        isStatusAfterUpdateApproved,
    } = useEditAgreementItem({
        currentAgreementItemId,
    });

    const isUniqInternalNumber = useIsUniqueContractChangeInternalNumber({
        contractId: currentContractId,
        contractChangeId: currentAgreementItemId,
        newInternalNumber: internalNumber,
        isCreating,
    });

    useEffect(() => {
        if (internalNumber && agreementItemData?.data?.internalNumber !== internalNumber && !isUniqInternalNumber) {
            setError('internalNumber', {
                message: 'Доп. соглашение с таким номером уже существует. Введите другой номер',
            });
        } else {
            clearErrors('internalNumber');
        }
    }, [agreementItemData?.data?.internalNumber, clearErrors, internalNumber, isUniqInternalNumber, setError]);

    const createOrEditStatus = useMemo(
        () => (isCreating ? createAgreementStatus : agreementEditStatus),
        [agreementEditStatus, createAgreementStatus, isCreating],
    );
    const createOrEditError = useMemo(
        () => (isCreating ? createAgreementError : agreementEditError),
        [agreementEditError, createAgreementError, isCreating],
    );
    const createOrEditData = useMemo(
        () => (isCreating ? createAgreementData : agreementEditData),
        [agreementEditData, createAgreementData, isCreating],
    );

    const onSaveErrorReport = useCallback(() => {
        ExportCSVReport(createOrEditData?.validation);
    }, [createOrEditData?.validation]);

    const createOrEditReport = useMemo(
        () => (isCreating ? onSaveErrorReport : undefined),
        [isCreating, onSaveErrorReport],
    );

    const createOrEditErrorTitle = useMemo(() => (isCreating ? 'Реквизиты ДС' : 'Изменение статуса'), [isCreating]);

    const handleCloseModal = useCallback(() => {
        if ((isCreating || !formState.isDirty) && !isSubmitted) {
            closeHandler?.(false);
        } else {
            setIsOpenWarningDialog(true);
        }
    }, [closeHandler, formState.isDirty, isCreating, isSubmitted]);

    const handleSubmitWarningDialog = useCallback(() => {
        closeHandler?.(false);
    }, [closeHandler]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const onCloseSaveErrorModal = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const status = useWatch({ control, name: 'status' });

    const statusDialogWarningText = useGetDialogWarningText({
        initialStatus: agreementItemData?.data?.status,
        nextStatus: status?.id,
    });

    useEffect(() => {
        if (formState.dirtyFields.status) {
            setIsOpenStatusDialog(true);
        }
    }, [formState.dirtyFields.status]);

    const handleSubmitStatusDialog = useCallback(() => {
        setIsOpenStatusDialog(false);
    }, []);

    const handleCancelStatusDialog = useCallback(() => {
        resetField('status');
        setIsOpenStatusDialog(false);
    }, [resetField]);

    const analyticParams = useMemo(
        () => ({
            projectId,
            contractId: currentContractId,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            currentContractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
        ],
    );

    const onSubmit = useCallback(
        (data) => {
            const variables = {
                contractId: currentContractId,
                status: data?.status?.id,
                subject: data?.subject,
                number: data?.internalNumber,
                signingDate: data?.signingDate,
            };

            if (isCreating) {
                dispatch(createAgreementRequest({ input: variables }));
            } else {
                dispatch(
                    agreementEditRequest({
                        contractChangeInput: {
                            ...variables,
                            contractChangeId: currentAgreementItemId,
                        },
                    }),
                );

                if (currentAgreementItemId) {
                    sendGpnAnalyticsEvent({
                        ...analyticParams,
                        componentId: 'rim_save DS button_pressed',
                        agreementId: currentAgreementItemId,
                        agreementStatus: data?.status?.id,
                    });
                }
            }

            setIsSubmitted(true);
        },
        [
            agreementEditRequest,
            analyticParams,
            createAgreementRequest,
            currentAgreementItemId,
            currentContractId,
            dispatch,
            isCreating,
            sendGpnAnalyticsEvent,
        ],
    );

    const handleSave = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, [formRef]);

    const [resetFiltersAfterUpdate] = useResetMultipleStoreData({
        requestIds: [
            'contractChangeSubjectFilter',
            'contractChangeStatusFilter',
            'contractChangeCreatedAtFilter',
            'contractChangeInternalNumberFilter',
            'contractChangeSigningDateFilter',
        ],
    });

    useEffect(() => {
        if (isSubmitted && createOrEditData?.result) {
            const newId = createOrEditData?.data?.id;

            resetFiltersAfterUpdate();
            if (isCreating && newId) {
                navigate(`${newId}`);

                sendGpnAnalyticsEvent({
                    ...analyticParams,
                    componentId: 'rim_DS file_saved',
                    agreementId: newId,
                    agreementStatus: ContractChangeStatusEnum.Formation,
                });
            }

            if (contractHasContractChanges) {
                refreshHandler?.();
            } else {
                refreshProject?.();
            }

            if (!needUpdateCurrentPriceList && needUpdatePriceListHandler && isStatusAfterUpdateApproved) {
                needUpdatePriceListHandler(true);
            }

            resetCreatingData(null);
            resetEditData();
            closeHandler?.(false);
        }
    }, [
        analyticParams,
        closeHandler,
        contractHasContractChanges,
        createOrEditData?.data?.id,
        createOrEditData?.result,
        isCreating,
        isStatusAfterUpdateApproved,
        isSubmitted,
        navigate,
        needUpdateCurrentPriceList,
        needUpdatePriceListHandler,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        refreshHandler,
        refreshProject,
        resetCreatingData,
        resetEditData,
        resetFiltersAfterUpdate,
        sendGpnAnalyticsEvent,
    ]);

    useEffect(() => {
        if (isCreating) {
            reset({ subject: '', status: statusItemFromId[ContractChangeStatusEnum.Formation] });
        } else {
            reset({
                subject: agreementItemData?.data?.subject,
                status: agreementItemData?.data?.status
                    ? statusItemFromId[agreementItemData.data.status]
                    : statusItemFromId[ContractChangeStatusEnum.Formation],
                internalNumber: agreementItemData?.data?.internalNumber,
                createdAt: formatDateToLocale(agreementItemData?.data?.createdAt),
                signingDate: newDate(agreementItemData?.data?.signingDate),
            });
        }
    }, [
        agreementItemData?.data?.createdAt,
        agreementItemData?.data?.internalNumber,
        agreementItemData?.data?.signingDate,
        agreementItemData?.data?.status,
        agreementItemData?.data?.subject,
        isCreating,
        reset,
    ]);

    const signingDateFieldDisabled = useMemo(
        () =>
            isCreating ||
            agreementItemData?.data?.status !== ContractChangeStatusEnum.Approving ||
            isDisabledByProjectStatus ||
            isDisabledByContractStatus,
        [agreementItemData?.data?.status, isCreating, isDisabledByContractStatus, isDisabledByProjectStatus],
    );

    const isNextStatusApproved = useMemo(() => status?.id === ContractChangeStatusEnum.Approved, [status]);
    const isCurrentStatusApproved = useMemo(
        () => agreementItemData?.data?.status === ContractChangeStatusEnum.Approved,
        [agreementItemData?.data?.status],
    );
    const isDelayedStatus = useMemo(
        () => agreementItemData?.data?.status === ContractChangeStatusEnum.Delayed,
        [agreementItemData?.data?.status],
    );
    const isCommit = useMemo(
        () => isCreatingInProgress || isSavingInProgress || isFetchingDataForDisplayInProgress,
        [isCreatingInProgress, isFetchingDataForDisplayInProgress, isSavingInProgress],
    );

    const statusItems = useMemo(() => {
        if (isCreating) {
            return [statusItemFromId[ContractChangeStatusEnum.Formation]];
        }

        const statusFollowers = (agreementItemData?.data?.statusFollowers || []).map((v) => statusItemFromId[v]);

        return [
            ...statusFollowers,
            ...(agreementItemData?.data?.status ? [statusItemFromId[agreementItemData?.data?.status]] : []),
        ];
    }, [agreementItemData?.data?.status, agreementItemData?.data?.statusFollowers, isCreating]);

    return (
        <ModalDialog
            isOpen={isModalOpened}
            title="Реквизиты ДС"
            onClose={handleCloseModal}
            onConfirm={handleSave}
            withTitleCloseButton
            style={{ maxWidth: '600px' }}
            isCommit={isCommit}
            isOnClickOutsideDisable
            btnConfirmDisabled={isDisabledByProjectStatus || isDisabledByContractStatus}
        >
            <ModalDialog
                title="Изменение статуса"
                isOpen={isOpenStatusDialog}
                onConfirm={handleSubmitStatusDialog}
                onClose={handleCancelStatusDialog}
                btnConfirmLabel="Изменить"
                btnCancelLabel="Не изменять"
                withTitleCloseButton
                style={{ width: 520 }}
                bodyMinHeight="min-height"
            >
                <div style={{ padding: 20, lineHeight: '24px' }}>{statusDialogWarningText}</div>
            </ModalDialog>

            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                <Grid rowGap="s" cols="1" style={{ maxWidth: '90%', padding: '24px' }}>
                    {isFetchingDataForDisplayInProgress ? (
                        <div style={{ height: '256px' }}>
                            <Loader />
                        </div>
                    ) : (
                        <>
                            <GridItem>
                                <ControlledTextField
                                    name="subject"
                                    label="Предмет ДС"
                                    control={control}
                                    type="text"
                                    directionTooltip="upRight"
                                    tooltipSize="m"
                                    rules={isNextStatusApproved ? requiredContractChangeApprovedRule : undefined}
                                    required={isNextStatusApproved}
                                    disabled={
                                        isCurrentStatusApproved ||
                                        isDelayedStatus ||
                                        isDisabledByProjectStatus ||
                                        isDisabledByContractStatus
                                    }
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledSelectField
                                    name="status"
                                    label="Статус ДС"
                                    control={control}
                                    items={statusItems}
                                    disabled={isCreating || isDisabledByProjectStatus || isDisabledByContractStatus}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="createdAt"
                                    label="Дата создания"
                                    control={control}
                                    type="text"
                                    disabled
                                    placeholder="ДД.ММ.ГГГГ"
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="internalNumber"
                                    label="Номер"
                                    control={control}
                                    type="text"
                                    directionTooltip="upRight"
                                    tooltipSize="m"
                                    rules={isNextStatusApproved ? requiredContractChangeApprovedRule : undefined}
                                    required={isNextStatusApproved}
                                    disabled={
                                        isCurrentStatusApproved ||
                                        isDelayedStatus ||
                                        isDisabledByProjectStatus ||
                                        isDisabledByContractStatus
                                    }
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledDateField
                                    control={control}
                                    label="Дата подписания"
                                    name="signingDate"
                                    type="date"
                                    directionTooltip="upRight"
                                    tooltipSize="m"
                                    disabled={signingDateFieldDisabled}
                                    rules={isNextStatusApproved ? requiredContractChangeApprovedRule : undefined}
                                    required={isNextStatusApproved || isDelayedStatus}
                                />
                            </GridItem>
                        </>
                    )}
                </Grid>
            </form>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={handleSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={handleCancelWarningDialog}
            />
            <ErrorHandlingModal
                titleText={createOrEditErrorTitle}
                customButtonText="Понятно"
                viewButton="primary"
                isSubmitted={isSubmitted}
                statusRequest={createOrEditStatus}
                dataRequest={createOrEditData}
                errorsRequest={createOrEditError}
                handleCreateReport={createOrEditReport}
                handleCloseModalError={onCloseSaveErrorModal}
                style={{ maxWidth: '520px' }}
            />
        </ModalDialog>
    );
};

AddOrEditAgreementModal.displayName = 'AddOrEditAgreementModal';
